import React, { useEffect, useState } from "react";
import { FormControl, FormHelperText, Icon, TextField } from "@mui/material";

import DialogBase from "./DialogBase";
import { Consortium } from "../../../models/consortium/consortium";
import { consortiumService } from "../../../services/consortium.service";

interface DialogConsortiumProps {
  open: boolean;
  selectedConsortiumId?: string | null;
  onClose: () => void;
  onSubmit: (consortium: Consortium) => void;
}

const DialogConsortium: React.FC<DialogConsortiumProps> = ({
  open,
  selectedConsortiumId,
  onClose,
  onSubmit,
}) => {
  const isEditMode = Boolean(selectedConsortiumId);

  const [consortium, setConsortium] = useState<Consortium>({
    id: null,
    name: "",
    description: "",
  });

  const [validationErrors, setValidationErrors] = useState<{
    name?: string;
  }>({});

  useEffect(() => {
    if (!open) return;

    const initialize = async () => {
      if (selectedConsortiumId) {
        try {
          const current = await consortiumService.getConsortium(selectedConsortiumId);
          setConsortium({
            id: current.id,
            name: current.name ?? "",
            description: current.description ?? "",
            members: current.members ?? [],
          });
        }
        catch (error) {
          console.error("Failed to load consortium:", error);
          setConsortium({ id: selectedConsortiumId, name: "", description: "" });
        }
      }
      else {
        setConsortium({ id: null, name: "", description: "" });
      }

      setValidationErrors({});
    };

    initialize();
  }, [open, selectedConsortiumId]);

  const onChange = <K extends keyof Consortium>(key: K, value: Consortium[K]) => {
    setConsortium((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = () => {
    const errors: { name?: string } = {};

    if (!consortium.name || consortium.name.trim().length < 1) {
      errors.name = "Naam is verplicht";
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    onSubmit({
      ...consortium,
      name: consortium.name.trim(),
      description: consortium.description?.trim() ?? "",
    });

    onClose();
  };

  return (
    <DialogBase
      open={open}
      title={isEditMode ? "Samenwerkingsverband wijzigen" : "Samenwerkingsverband toevoegen"}
      contentText=""
      onClose={onClose}
      onConfirm={handleSubmit}
      confirmButtonText="Opslaan"
      confirmButtonIcon={<Icon>save</Icon>}
    >
      <FormControl fullWidth required sx={{ my: 1 }}>
        <TextField
          required
          label="Naam"
          variant="filled"
          value={consortium.name ?? ""}
          placeholder="Bijv. Regionaal Energie Consortium"
          onChange={(e) => onChange("name", e.target.value)}
          error={!!validationErrors.name}
          helperText={validationErrors.name}
        />
      </FormControl>

      <FormControl fullWidth sx={{ my: 1 }}>
        <TextField
          label="Omschrijving"
          variant="filled"
          multiline
          rows={4}
          value={consortium.description ?? ""}
          placeholder="Beschrijf kort het doel of de context van dit samenwerkingsverband"
          onChange={(e) => onChange("description", e.target.value)}
        />
        {/* <FormHelperText>
          Optioneel
        </FormHelperText> */}
      </FormControl>
    </DialogBase>
  );
};

export default DialogConsortium;
